import { createTempChatRoomMessage } from "@/react/components/Chat/ChatWindowV2/helpers";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { ChatThreadsSliceState } from "../../createThreadsSlice";
import type {
  ChatRoomActionsSliceState,
  ChatRoomMessageActionsSliceState,
  ChatRoomsSliceState,
  CreateMessageParams,
} from "../types";
import {
  BATCH_DELAY_MS,
  batchQueues,
  processBatchedMessages,
} from "./batchingMessages";

interface MessageDataActionsProps {
  appendLastMessage: (uuid: string, message: ChatRoomMessage) => void;
  clearSpotlightedMessages: (uuid: string) => void;
  deleteMessage: (uuid: string, messageId: number) => void;
  sendRichTextMessage: (
    uuid: string,
    params: {
      richTextBody: any;
      localAttachments: any[];
      localSgidToObjectMap: any;
      parentMessageId?: number;
      unfurlUrls?: { [key: string]: boolean };
    },
  ) => Promise<void>;
  setChatRoomMessages: (uuid: string, messages: ChatRoomMessage[]) => void;
  updateMessage: (uuid: string, message: ChatRoomMessage) => void;
}

export const messageDataActions = (
  set: (fn: (state: ChatRoomsSliceState) => void) => void,
  get: () => ChatRoomsSliceState &
    ChatRoomActionsSliceState &
    ChatRoomMessageActionsSliceState &
    ChatThreadsSliceState,
): MessageDataActionsProps => ({
  setChatRoomMessages: (uuid, messages) =>
    set((state: ChatRoomsSliceState) => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = messages;
      }
    }),
  appendLastMessage: (uuid: string, message: ChatRoomMessage) => {
    // Get or create batch queue for this chat room
    if (!batchQueues.has(uuid)) {
      batchQueues.set(uuid, { messages: [], timeoutId: null });
    }

    const queue = batchQueues.get(uuid);
    if (!queue) {
      return;
    }

    // Add message to batch queue
    queue.messages.push({
      message,
    });

    if (message.id < 0) {
      // Display optimistic messages immediately
      void processBatchedMessages(uuid, set, get);
      queue.timeoutId = null;
    } else if (!queue.timeoutId) {
      // Only set timeout if one doesn't already exist
      queue.timeoutId = setTimeout(() => {
        void processBatchedMessages(uuid, set, get);
      }, BATCH_DELAY_MS);
    }
  },
  updateMessage: (uuid: string, message: ChatRoomMessage) =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        const indexOfMessage = chatRoomState.chatRoomMessages.findIndex(
          (currentMessage: ChatRoomMessage) => currentMessage.id === message.id,
        );
        if (indexOfMessage > -1) {
          chatRoomState.chatRoomMessages[indexOfMessage] = {
            ...chatRoomState.chatRoomMessages[indexOfMessage],
            ...message,
          };
        }
      }
    }),
  deleteMessage: (uuid: string, messageId: number) =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = chatRoomState.chatRoomMessages.filter(
          (currentMessage: ChatRoomMessage) => currentMessage.id !== messageId,
        );
      }
    }),
  clearSpotlightedMessages: uuid =>
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (chatRoomState) {
        chatRoomState.chatRoomMessages = chatRoomState.chatRoomMessages.map(
          (message: ChatRoomMessage) => ({
            ...message,
            spotlighted: false,
          }),
        );
      }
    }),
  sendRichTextMessage: async (uuid, params) => {
    const {
      richTextBody,
      localAttachments,
      localSgidToObjectMap,
      parentMessageId,
      unfurlUrls,
    } = params;
    await get().actions.ensureChatRoomParticipant(uuid);
    const chatRoom = get().chatRoomMap.get(uuid);
    const currentParticipant = chatRoom?.current_participant;
    const currentParticipantId = currentParticipant?.id;
    if (!chatRoom || !currentParticipant || !currentParticipantId) {
      return;
    }
    const createMessageParams: CreateMessageParams = {
      chat_room_participant_id: currentParticipantId,
      rich_text_body: richTextBody,
    };
    if (parentMessageId) {
      createMessageParams.parent_message_id = parentMessageId;
    }

    if (unfurlUrls) {
      createMessageParams.unfurl_urls = unfurlUrls;
    }

    const res = await reactQueryPost(
      internalApi.chatRoomMessages.create({ uuid }),
      {
        chat_room_message: createMessageParams,
      },
    );
    const tempMessage = createTempChatRoomMessage({
      chatRoom,
      richTextBody,
      localAttachments,
      localSgidToObjectMap,
      unfurlUrls,
      parentMessageId,
      creationUuid: res.creation_uuid,
      sentAt: res.sent_at,
    });
    if (!tempMessage) {
      return;
    }
    if (parentMessageId) {
      get().threads.appendLastMessage(tempMessage);
    } else {
      get().messageActions.appendLastMessage(uuid, tempMessage);
    }
  },
});
